import { useEffect, useMemo, useRef, useState } from "react";
import {
  GUIDE,
  SECTIONS,
  sectionOf,
  type Block,
  type Shot,
} from "../guide/content";
import { routedClick } from "../router";

export const DOCS_ROOT = "/docs";
export const DOCS_HOME = GUIDE[0]!.path;

/** True for any address the guide owns. */
export function isDocsPath(path: string): boolean {
  return path === DOCS_ROOT || path.startsWith(`${DOCS_ROOT}/`);
}

interface Props {
  /** The current address. An unknown docs page falls back to the first. */
  path: string;
  navigate: (path: string) => void;
}

/**
 * A screenshot, collapsed to its caption until asked for.
 *
 * Pictures of a UI are reference material, not the argument - someone reading
 * the steps in order should be able to keep reading without scrolling past
 * seven screenshots, and someone who cannot tell which control is meant
 * should be one click from seeing it.
 */
function Screenshot({ shot }: { shot: Shot }) {
  const [open, setOpen] = useState(false);

  return (
    <figure className={`shot ${open ? "open" : ""}`}>
      <figcaption>{shot.caption}</figcaption>
      <button
        type="button"
        className="shot-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "− Collapse image" : "+ Expand image"}
      </button>
      <img src={shot.src} alt={shot.alt} loading="lazy" />
    </figure>
  );
}

function Body({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case "h2":
            return <h2 key={i}>{block.text}</h2>;
          case "h3":
            return <h3 key={i}>{block.text}</h3>;
          case "p":
            return <p key={i}>{block.text}</p>;
          case "ul":
            return (
              <ul key={i}>
                {block.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            );
          case "code":
            return (
              <pre key={i} className="doc-code">
                <code>{block.text}</code>
              </pre>
            );
          case "screenshot":
            return <Screenshot key={i} shot={block.shot} />;
          case "callout":
            return (
              <div key={i} className={`doc-callout ${block.tone}`}>
                <span className="doc-callout-icon" aria-hidden="true">
                  {block.tone === "success" ? "✓" : block.tone === "info" ? "i" : "!"}
                </span>
                <span>
                  {block.title && <strong>{block.title} </strong>}
                  {block.text}
                </span>
              </div>
            );
          case "table":
            return (
              <div key={i} className="doc-table-wrap">
                <table className="doc-table">
                  <thead>
                    <tr>
                      {block.headers.map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row) => (
                      <tr key={row[0]}>
                        {row.map((cell, c) => (
                          <td key={c}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "walkthrough":
            return (
              <ol key={i} className="doc-walkthrough">
                {block.items.map((item) => (
                  <li key={item.text}>
                    <span>{item.text}</span>
                    {item.shot && <Screenshot shot={item.shot} />}
                  </li>
                ))}
              </ol>
            );
        }
      })}
    </>
  );
}

/**
 * The guide: its own page, with its own chrome.
 *
 * Not a modal over the app. It is read through rather than glanced at, and a
 * dialog that has to be dismissed before you can try the step you just read
 * is the wrong shape for that. The dark bar and the contents rail are the
 * same arrangement AWS's own workshop guides use, which is what this is for.
 */
export function Guide({ path, navigate }: Props) {
  // An unknown or bare /docs address shows the first page rather than an
  // error: there is nothing useful to say about a mistyped guide URL that
  // beats the beginning of the guide.
  const index = Math.max(
    0,
    GUIDE.findIndex((p) => p.path === path),
  );
  const page = GUIDE[index]!;
  const section = sectionOf(page.id);
  const previous = index > 0 ? GUIDE[index - 1] : undefined;
  const next = index < GUIDE.length - 1 ? GUIDE[index + 1] : undefined;

  const [query, setQuery] = useState("");
  const main = useRef<HTMLElement>(null);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.map((s) => ({
      ...s,
      pages: s.pages.filter(
        (p) =>
          p.label.toLowerCase().includes(q) ||
          p.summary.toLowerCase().includes(q) ||
          (s.label?.toLowerCase().includes(q) ?? false),
      ),
    })).filter((s) => s.pages.length);
  }, [query]);

  // A new page starts at its own beginning, not wherever the last one was
  // scrolled to.
  useEffect(() => {
    main.current?.scrollTo({ top: 0 });
  }, [path]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") navigate("/");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <div className="docs">
      <header className="docs-bar">
        <a href="/" className="docs-brand" onClick={routedClick(navigate, "/")}>
          <span className="docs-mark">CX</span>
          <span className="docs-brand-name">Lex {"→"} CX Designer</span>
          <span className="docs-brand-sub">Migration guide</span>
        </a>
        <a
          href="/"
          className="docs-exit"
          onClick={routedClick(navigate, "/")}
        >
          Back to the tool {"→"}
        </a>
      </header>

      <div className="docs-body">
        <nav className="docs-nav" aria-label="Guide contents">
          <p className="docs-nav-title">
            Migrating an Amazon Lex V2 bot to Amazon Connect CX Designer
          </p>

          <div className="docs-filter">
            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
              <path
                d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z M20 20l-4-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter pages"
              aria-label="Filter pages"
            />
          </div>

          {shown.map((entry) => (
            <div key={entry.id} className="docs-nav-group">
              {entry.label && (
                <p className="docs-nav-group-label">{entry.label}</p>
              )}
              <ul>
                {entry.pages.map((p) => (
                  <li key={p.id}>
                    <a
                      href={p.path}
                      className={p.id === page.id ? "active" : ""}
                      onClick={routedClick(navigate, p.path)}
                      aria-current={p.id === page.id ? "page" : undefined}
                    >
                      {p.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {!shown.length && (
            <p className="docs-nav-empty">No pages match "{query.trim()}"</p>
          )}
        </nav>

        <main className="docs-main" ref={main}>
          <div className="docs-inner">
            <p className="docs-crumb">
              <a
                href={DOCS_HOME}
                onClick={routedClick(navigate, DOCS_HOME)}
              >
                Guide
              </a>
              {section?.label && (
                <>
                  <span aria-hidden="true"> {"›"} </span>
                  <span>{section.label}</span>
                </>
              )}
              <span aria-hidden="true"> {"›"} </span>
              <span className="here">{page.label}</span>
            </p>

            <h1 className="docs-title">{page.title}</h1>
            <p className="docs-summary">{page.summary}</p>
            <hr className="docs-rule" />

            <div className="doc-body">
              <Body blocks={page.body} />
            </div>

            <div className="docs-pager">
              {previous ? (
                <a
                  href={previous.path}
                  className="docs-prev"
                  onClick={routedClick(navigate, previous.path)}
                >
                  {"←"} {previous.label}
                </a>
              ) : (
                <span />
              )}
              {next ? (
                <a
                  href={next.path}
                  className="docs-next"
                  onClick={routedClick(navigate, next.path)}
                >
                  {next.label} {"→"}
                </a>
              ) : (
                <a
                  href="/"
                  className="docs-next"
                  onClick={routedClick(navigate, "/")}
                >
                  Start a migration {"→"}
                </a>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
